import { useCallback, useState } from "react";
import {
  fetchMonthlyReport,
  MonthlyReportMeta,
  MonthlyReportRow,
} from "../MonthlyReport/api/fetchMonthlyReport";

/**
 * Fetch state machine for the monthly scoreboard extract. The Monthly Report
 * table and the Trade Leaderboards both run on this data; each feature hook
 * layers its own derivation on top.
 *
 * Error *messaging* is intentionally omitted: per the house convention the
 * global axios interceptor toasts on 4xx/5xx, so we store no error message.
 * We do, however, expose a boolean `failed` so consumers can render a distinct
 * "couldn't load — try again" affordance instead of the empty-month placeholder
 * (a failed run and a genuinely empty month must not look identical). The
 * empty-month case is a normal 200 with `count: 0` (`failed` stays false).
 *
 * `onReset` fires after every completed run — success or failure — so view
 * state tied to the previous data set (page index, sorting) is cleared in both
 * cases. Pass a stable (useCallback) reference.
 *
 * `runNonce` increments once per completed run (success or failure). It gives
 * consumers a run *identity* that changes even when the same month is re-run —
 * `meta.monthLabel` alone can't distinguish "re-ran January" from "still showing
 * January", so selection/marker resets that must fire on every run key off this.
 */
export const useMonthlyReportData = (onReset?: () => void) => {
  const [rows, setRows] = useState<MonthlyReportRow[]>([]);
  const [meta, setMeta] = useState<MonthlyReportMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [failed, setFailed] = useState(false);
  const [runNonce, setRunNonce] = useState(0);

  const run = useCallback(
    async (year: number, month: number) => {
      setLoading(true);
      setFailed(false);
      try {
        const result = await fetchMonthlyReport({ year, month });
        setRows(result.data);
        setMeta(result.meta);
        setHasRun(true);
        onReset?.();
      } catch {
        // The global axios interceptor toasts the error; here we surface a
        // distinct failure state (not the empty-month placeholder) and clear
        // the stale data so a failed run never leaves the previous month's rows
        // rendered behind the newly-selected pickers. View state resets too, so
        // stale page/sort don't carry over onto the retry.
        setRows([]);
        setMeta(null);
        setFailed(true);
        setHasRun(true);
        onReset?.();
      } finally {
        // Bump the run identity for both paths — a re-run of the same month
        // still counts as a fresh run for consumers keying resets off it.
        setRunNonce((n) => n + 1);
        setLoading(false);
      }
    },
    [onReset],
  );

  return { failed, hasRun, loading, meta, rows, run, runNonce };
};
